import { Checkbox } from '@any-ui-react/core';
import { HeaderTr, Table, TableContainer, TdBody, Th, Thead, TrBody } from '@any-ui-react/table';
import classNames from 'classnames';
import type {
  CSSProperties,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  RefAttributes,
  RefObject,
  TableHTMLAttributes,
} from 'react';

import type { TableLayout } from '@/common/components/shared/table/Table';
import { useFloatingHeader } from '@/common/hooks/table';

import { DEFAULT_COLUMN_MAX_WIDTH } from '../constants';
import { TableSelectionBar, type TableSelectionContextType, useTableSelectionCtx } from '../selection';
import type { TableDesktopProps } from './TableDesktop';

export function TableDesktopContainer<T extends Record<string, unknown>>({
  allowFloatingHeader = false,
  children,
  data,
  fullWidth = true,
  headerClassName,
  hoverable = true,
  layout,
  numOfStickyCols = 1,
  rowClassName,
  scrollContainerClassName,
  tableLayout = 'auto',
}: PropsWithChildren<TableDesktopProps<T>>): ReactElement {
  const {
    floatingContainerRefProps,
    tableContainerRef,
    scrollContainerRefProps,
    floatingHeaderRefProps,
    thProps,
    tableRef,
    showFloatingHeader,
  } = useFloatingHeader();
  const { selectableData, selection, selectable, toggleSelectAll, toggleSelection, currentPageSelection, checkAll } =
    useTableSelectionCtx<T>();

  const hasFooter = layout.some(item => item.footer);

  return (
    <TableContainer
      ref={tableContainerRef}
      className={classNames(
        'border-none shadow-none',
        { 'rounded-b': data?.length },
        { 'h-full': allowFloatingHeader },
      )}
      numOfStickyCols={numOfStickyCols}
      tableContainerClassName="h-full"
    >
      <div
        className={classNames('overflow-auto', { 'w-full': fullWidth }, scrollContainerClassName)}
        {...scrollContainerRefProps}
      >
        {allowFloatingHeader && showFloatingHeader && (
          <SelectAllHeader
            checkAll={checkAll}
            currentPageSelection={currentPageSelection}
            floatingContainerRefProps={floatingContainerRefProps}
            floatingHeaderRefProps={floatingHeaderRefProps}
            headerClassName={headerClassName}
            layout={layout}
            numOfStickyCols={numOfStickyCols}
            selectable={selectable}
            selectableData={selectableData}
            selection={selection}
            thProps={thProps}
            toggleSelectAll={toggleSelectAll}
            toggleSelection={toggleSelection}
          />
        )}
        <Table
          ref={tableRef.current ? (tableRef as RefObject<HTMLTableElement>) : undefined}
          className="z-0 border-separate border-spacing-0"
          style={{ tableLayout }}
        >
          {/* Header */}
          <Thead className={classNames({ 'sticky top-0 z-20': allowFloatingHeader, relative: !allowFloatingHeader })}>
            <HeaderTr className={classNames(headerClassName)}>
              {layout.map(({ headerRender, accessor, thClassName, minWidth, maxWidth, width }, i) => {
                const hasCheckbox = i === 0 && selectable?.byField && selection;
                return (
                  <Th
                    key={accessor}
                    className={classNames(
                      'whitespace-nowrap border-y border-gray-2 text-left text-2xs px-4',
                      thClassName,
                      { 'sticky top-0 bg-inherit': allowFloatingHeader && !selection?.items.length },
                      { 'z-20': allowFloatingHeader && !selection?.items.length && i < numOfStickyCols },
                      { 'z-10': allowFloatingHeader && !selection?.items.length && i >= numOfStickyCols },
                    )}
                    index={i}
                    style={{
                      minWidth: width ? '' : minWidth || 'none',
                      maxWidth: width ? '' : maxWidth || `${DEFAULT_COLUMN_MAX_WIDTH}px`,
                      width: width || 'auto',
                    }}
                    width={width || 'auto'}
                  >
                    {hasCheckbox && selection.items.length === 0 ? (
                      <CellWithCheckbox checkAll={checkAll} selection={selection}>
                        {headerRender()}
                      </CellWithCheckbox>
                    ) : (
                      headerRender()
                    )}
                  </Th>
                );
              })}
            </HeaderTr>
            {(selection?.items?.length || 0) > 0 && (
              <HeaderTr className={classNames('absolute', 'top-0', 'h-full', 'w-full', headerClassName)}>
                <TableSelectionBar
                  allPagesSelectable={selectable?.allPagesSelectable}
                  aside={selectable?.aside}
                  currentPageSelection={currentPageSelection.length}
                  maxSelection={selectableData?.length || 0}
                  total={selectable?.total || 0}
                  onToggleAllPages={toggleSelectAll}
                  onToggleSelection={toggleSelection}
                />
              </HeaderTr>
            )}
          </Thead>
          {/* Body */}
          <tbody>{children}</tbody>
          {/* Footer */}
          {hasFooter && (
            <tfoot>
              <TrBody
                key={data.length}
                className={classNames({
                  'hover:white group bg-gray-0': hoverable,
                  'hover:bg-gray-0': !hoverable,
                })}
              >
                {layout.map(({ footer, accessor, minWidth, maxWidth, width, tdClassName, truncated = true }) => {
                  const resolvedTdClassName = typeof tdClassName === 'function' ? tdClassName(null) : tdClassName;
                  return (
                    <TdBody
                      key={accessor}
                      className={classNames(rowClassName, resolvedTdClassName, { truncate: truncated })}
                      style={{
                        minWidth: width ? '' : minWidth || 'none',
                        maxWidth: width ? '' : maxWidth || `${DEFAULT_COLUMN_MAX_WIDTH}px`,
                        background: 'inherit',
                      }}
                      width={width || 'auto'}
                    >
                      {footer?.()}
                    </TdBody>
                  );
                })}
              </TrBody>
            </tfoot>
          )}
        </Table>
      </div>
    </TableContainer>
  );
}

interface SelectAllHeaderProps<T extends Record<string, unknown>> {
  checkAll: TableSelectionContextType<T>['checkAll'];
  currentPageSelection: TableSelectionContextType<T>['currentPageSelection'];
  floatingContainerRefProps: TableHTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement>;
  floatingHeaderRefProps: TableHTMLAttributes<HTMLTableElement> & RefAttributes<HTMLTableElement>;
  headerClassName: string | undefined;
  layout: ReadonlyArray<TableLayout<T>>;
  numOfStickyCols: number;
  selectable: TableSelectionContextType<T>['selectable'];
  selectableData: TableSelectionContextType<T>['selectableData'];
  selection: TableSelectionContextType<T>['selection'];
  thProps: (index: number) => { style: CSSProperties };
  toggleSelectAll: TableSelectionContextType<T>['toggleSelectAll'];
  toggleSelection: TableSelectionContextType<T>['toggleSelection'];
}

const SelectAllHeader = <T extends Record<string, unknown>>({
  checkAll,
  currentPageSelection,
  floatingContainerRefProps,
  floatingHeaderRefProps,
  headerClassName,
  layout,
  numOfStickyCols,
  selectable,
  selectableData,
  selection,
  thProps,
  toggleSelectAll,
  toggleSelection,
}: SelectAllHeaderProps<T>) => (
  <div className="fixed top-0 z-10" {...floatingContainerRefProps}>
    <div className="absolute flex w-full border-spacing-0 flex-col overflow-hidden">
      <Table {...floatingHeaderRefProps}>
        <Thead className="relative">
          <HeaderTr className={classNames(headerClassName)}>
            {layout.map(({ headerRender, accessor, thClassName, minWidth, maxWidth, width }, i) => {
              const hasCheckbox = i === 0 && selectable?.byField && selection;
              return (
                <Th
                  key={accessor}
                  className={classNames(
                    'sticky top-0 whitespace-nowrap border-y border-t-0 border-gray-2 bg-inherit text-left text-2xs',
                    thClassName,
                    { 'z-[2]': !selection?.items.length && i < numOfStickyCols },
                  )}
                  index={i}
                  style={{
                    ...thProps(i).style,
                    minWidth: width ? '' : minWidth || 'none',
                    maxWidth: width ? '' : maxWidth || `${DEFAULT_COLUMN_MAX_WIDTH}px`,
                  }}
                  width={width || 'auto'}
                >
                  {hasCheckbox && selection.items.length === 0 ? (
                    <div className="flex items-center">
                      <div
                        className="-ml-4 cursor-pointer p-4"
                        onClick={e => {
                          e.stopPropagation();
                          if (selection?.items.length === 0) {
                            checkAll();
                          }
                        }}
                      >
                        <Checkbox
                          checked={(selection?.items?.length || 0) > 0}
                          onChange={e => {
                            if (e.currentTarget.checked) {
                              checkAll();
                            }
                          }}
                        />
                      </div>
                      {headerRender()}
                    </div>
                  ) : (
                    headerRender()
                  )}
                </Th>
              );
            })}
          </HeaderTr>
          {(selection?.items?.length || 0) > 0 && (
            <HeaderTr className={classNames('absolute', 'top-0', 'h-full', 'w-full', headerClassName)}>
              <TableSelectionBar
                allPagesSelectable={selectable?.allPagesSelectable}
                aside={selectable?.aside}
                currentPageSelection={currentPageSelection.length}
                maxSelection={selectableData?.length || 0}
                total={selectable?.total || 0}
                onToggleAllPages={toggleSelectAll}
                onToggleSelection={toggleSelection}
              />
            </HeaderTr>
          )}
        </Thead>
      </Table>
    </div>
  </div>
);

interface CellWithCheckboxProps<T> {
  checkAll: TableSelectionContextType<T>['checkAll'];
  children: ReactNode;
  selection: NonNullable<TableSelectionContextType<T>['selection']>;
}

const CellWithCheckbox = <T,>({ checkAll, children, selection }: CellWithCheckboxProps<T>) => (
  <div className="flex items-center">
    <div
      className="-ml-4 cursor-pointer p-4"
      onClick={e => {
        e.stopPropagation();
        if (selection.items.length === 0) {
          checkAll();
        }
      }}
    >
      <Checkbox
        checked={selection.items.length > 0}
        onChange={e => {
          if (e.currentTarget.checked) {
            checkAll();
          }
        }}
      />
    </div>
    {children}
  </div>
);
